import React, { useMemo } from 'react';
import { MeetingRoomBookingDialogHeader } from "./MeetingRoomBookingDialogHeader";
import { BodyLarge, BodyRegular } from '@shared/ui/atoms/fonts/Body';
import {
  cancelBooking,
  cancelSeriesBooking,
  confirmBooking,
  MeetingRoomBookingTab,
  selectActiveBookingTab,
  selectApplyToSeries,
  selectExistingMeetingRoomBooking,
  selectIsSavingMeetingBooking,
  selectMeetingRoom,
  selectRecurrence,
  selectRootOfficeForMeetingRoom,
  setApplyToSeries,
  updateBooking
} from "../../models/meeting-room-booking-dialog.duck";
import { HorizontalSpacer } from '@shared/ui/atoms/VerticalSpacer';
import Colours from '@shared/ui/atoms/Colours';
import { PrimaryButton } from '@shared/ui/atoms/buttons/PrimaryButton';
import { closeDialog } from '@shared/store/dialog.duck';
import { useDispatch, useSelector } from "react-redux";
import { WithOfficeInformation } from '@features/desks/hooks/WithOfficeInformation';
import { selectActiveDay } from '@features/whereabouts/store/editMovements.duck';
import { selectCurrentUser, selectIsCalendarSyncActive } from '@shared/store/auth.duck';
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { MeetingRoomBookingTabBookingView } from "./MeetingRoomBookingTabBookingView";
import { MeetingRoomBookingTabAttendeesView } from "./MeetingRoomBookingTabAttendeesView";
import { MeetingRoomBookingTabHospitalityView } from "./MeetingRoomBookingTabHospitalityView";
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { failureNotification, warningNotification } from '@shared/store/notification.duck';
import { MeetingRoomBookingTabDebugView } from "./MeetingRoomBookingTabDebugView";
import { largeMobile } from '@shared/ui/atoms/MediaQueries';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { isCompanyAdminUser, isOfficeAdminUser } from '@shared/utils/AccessControl';
import { IsSuperAdmin } from '@shared/hooks/IsSuperAdmin';
import { Checkbox } from '@shared/ui/atoms/Checkbox';

export function MeetingRoomBookingPanel(props: Props) {
  const dispatch = useDispatch();
  const activeDay = useSelector(selectActiveDay);
  const calendarSyncActive = useSelector(selectIsCalendarSyncActive);
  const activeTab = useSelector(selectActiveBookingTab);
  const meetingRoom = useSelector(selectMeetingRoom);
  const rootOffice = useSelector(selectRootOfficeForMeetingRoom);
  const isSavingMeetingRoomBooking = useSelector(selectIsSavingMeetingBooking);
  const existingMeetingRoomBooking = useSelector(selectExistingMeetingRoomBooking);
  const currentUser = useSelector(selectCurrentUser);
  const applyToSeries = useSelector(selectApplyToSeries);
  const recurrence = useSelector(selectRecurrence);
  const {t} = useTranslation();
  const isUserSuperAdmin = IsSuperAdmin();

  const showApplyToSeries = useMemo(() => {
    if (existingMeetingRoomBooking) {
      return !!existingMeetingRoomBooking.isRecurringMeeting;
    }
    return !!recurrence;
  }, [existingMeetingRoomBooking, recurrence]);

  const canCancelBooking = useMemo(() => {
    if (existingMeetingRoomBooking?.bookingUserId === currentUser?.id) {
      return true;
    }
    if (isCompanyAdminUser(currentUser) || isOfficeAdminUser(currentUser)) {
      return existingMeetingRoomBooking?.teamTodayMeetingId || (existingMeetingRoomBooking?.outlookEventUid && existingMeetingRoomBooking.outlookMeetingRoomId)
    }
    return false;
  }, [currentUser, existingMeetingRoomBooking]);

  const canSaveBooking = useMemo(() => {
    if (existingMeetingRoomBooking) {
      if (isCompanyAdminUser(currentUser) || isOfficeAdminUser(currentUser)) {
        return true
      }
      if (existingMeetingRoomBooking?.bookingUserId === currentUser?.id) {
        return true;
      }
      return false;
    }
    return true;
  }, [currentUser, existingMeetingRoomBooking])

  const officeInfo = WithOfficeInformation(meetingRoom?.officeId);

  const isOutlookRoom = useMemo(() => {
    return !!meetingRoom?.microsoftRoomId;
  }, [meetingRoom]);


  const onConfirmClicked = () => {
    if (existingMeetingRoomBooking) {
      if (existingMeetingRoomBooking.teamTodayMeetingId || (!existingMeetingRoomBooking.teamTodayMeetingId && existingMeetingRoomBooking.outlookBooking)) {
        if (isOutlookRoom && !rootOffice?.hospitalityEnabled) {
          dispatch(warningNotification('Cannot edit Outlook meeting bookings'));
        } else {
          if (existingMeetingRoomBooking.outlookEventUid && !!existingMeetingRoomBooking.teamTodayMeetingId) {
            dispatch(updateBooking({
              isOutlookRoom: isOutlookRoom, calendarSyncActive: calendarSyncActive, activeDay: activeDay
            }));
          } else if (!isOutlookRoom && !!existingMeetingRoomBooking.teamTodayMeetingRoomId) {
            dispatch(updateBooking({
              isOutlookRoom: isOutlookRoom, calendarSyncActive: calendarSyncActive, activeDay: activeDay
            }));
          } else {
            dispatch(confirmBooking({
              isOutlookRoom: isOutlookRoom, calendarSyncActive: calendarSyncActive, activeDay: activeDay
            }));
          }
        }
      } else {
        dispatch(failureNotification('Cannot update, no booking reference id'))
      }
    } else {
      dispatch(confirmBooking({
        isOutlookRoom: isOutlookRoom, calendarSyncActive: calendarSyncActive, activeDay: activeDay
      }));
    }
  }

  const onCancelClicked = () => {
    if (canCancelBooking) {
      dispatch(cancelBooking({}));
    }
  }

  const onCancelSeriesClicked = () => {
    if (canCancelBooking) {
      dispatch(cancelSeriesBooking({}));
    }
  }

  if (!meetingRoom) {
    return null;
  }

  return (
    <>
      <MeetingRoomBookingDialogHeader meetingRoom={meetingRoom} office={officeInfo} />

      <Content>

        {isOutlookRoom && !calendarSyncActive && <>
          <BodyLarge weight={600}>{t('room-booking-dialog.cannot-book-outlook-room')}</BodyLarge>
          <BodyRegular style={{marginBottom: 24}}>{t('room-booking-dialog.cannot-book-outlook-room-description')}</BodyRegular>
        </>}

        {(!isOutlookRoom || (isOutlookRoom && calendarSyncActive)) ? <>

          {isSavingMeetingRoomBooking ? <>
            <Center>
              <LoadingSpinner fullScreen={false} message={'Updating booking...'} hideBorder={true} />
            </Center>
          </> : <>
            {activeTab === MeetingRoomBookingTab.Booking && <MeetingRoomBookingTabBookingView userCanSaveBooking={canSaveBooking} />}
            {activeTab === MeetingRoomBookingTab.Attendees && <MeetingRoomBookingTabAttendeesView userCanSaveBooking={canSaveBooking} />}
            {activeTab === MeetingRoomBookingTab.Hospitality && <MeetingRoomBookingTabHospitalityView userCanSaveBooking={canSaveBooking} />}
            {activeTab === MeetingRoomBookingTab.Debug && isUserSuperAdmin && <MeetingRoomBookingTabDebugView userCanSaveBooking={canSaveBooking} />}
          </>}

          <HorizontalSpacer colour={Colours.lightGrey} style={{marginTop: 'auto'}} />

          {showApplyToSeries && <ApplyToSeriesRow>
            <Checkbox text={'room-booking-dialog.apply-to-series'}
                      value={applyToSeries}
                      disabled={isSavingMeetingRoomBooking || !canSaveBooking}
                      onChange={(checked: boolean) => dispatch(setApplyToSeries(checked))} />
          </ApplyToSeriesRow>}

          <ControlsRow>

            <PrimaryButton click={() => onConfirmClicked()}
                           disabled={isSavingMeetingRoomBooking || !canSaveBooking}
                           text={existingMeetingRoomBooking ? 'room-booking-dialog.update-booking' : 'room-booking-dialog.confirm-booking'} />

            {canCancelBooking && <CancelButtons>
              {existingMeetingRoomBooking?.isRecurringMeeting && <OutlineButton disabled={isSavingMeetingRoomBooking || !canSaveBooking}
                                                                                click={onCancelSeriesClicked}
                                                                                style={{width: 130}}
                                                                                text={'room-booking-dialog.cancel-series'}  />}

              <OutlineButton disabled={isSavingMeetingRoomBooking}
                             click={onCancelClicked}
                             text={'room-booking-dialog.cancel-booking'}  />
            </CancelButtons>}
          </ControlsRow>
        </> : <>

          <PrimaryButton click={() => dispatch(closeDialog())}
                         text={'button.close'}
                         style={{marginTop: 64}} />

        </>}
      </Content>
    </>
  )
}

interface Props {
}


const Content = styled.div`
  padding: 24px 16px 24px 16px;
  flex: 1;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  @media (${largeMobile}) {
    padding: 24px 56px 24px 56px;
  }
`

const ControlsRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const ApplyToSeriesRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  margin: 0 0 16px 0;
`


const CancelButtons = styled.div`
  display: flex;
  margin-left: auto;
  button {
    width: 150px; 
      &:nth-child(2) {
        margin-left: 8px;
      }
  }
`
